import React, { createContext, useContext, useState, useCallback, ReactNode, useEffect, useMemo } from 'react';
import { v4 as uuidv4 } from 'uuid';
import { Cart, CartItem, Fee, PricingTier, QuestionnaireResponse, SignatureData } from '../types';
import { useAuth } from './AuthContext';
import { useData } from './DataContext';
import { useSchoolSettings } from './SchoolSettingsContext';

// NOTE: When a fee is purchased with a partial payment (partialPaymentAmount < totalPrice),
// it should create a PreAssessedFee record in the database so the user can make
// additional payments from the Dashboard. This requires database persistence.

interface ActivityPassInfo {
  activityPassQty: number;
  regularQty: number;
  activityPassPrice: number;
  regularPrice: number;
  totalPrice: number;
  activityPassDiscount: number;
}

interface CartContextType {
  cart: Cart;
  addToCart: (fee: Fee, quantity: number, pricingTier: PricingTier, questionnaireResponses?: QuestionnaireResponse, signatureData?: SignatureData, partialPaymentAmount?: number, customUnitPrice?: number, partialPaymentPercentage?: number, amountPaid?: number, activityPassInfo?: ActivityPassInfo) => void;
  removeFromCart: (itemId: string) => void;
  updateQuantity: (itemId: string, quantity: number) => void;
  updatePartialPayment: (itemId: string, partialPaymentAmount: number | undefined) => void;
  clearCart: () => void;
  itemCount: number;
  hasActivityPass: boolean;
  getActivityPassQuantityUsed: (feeId: string) => number;
  getActivityPassQuantityRemaining: (feeId: string) => number;
}

const CartContext = createContext<CartContextType | undefined>(undefined);

export const useCart = () => {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error('useCart must be used within a CartProvider');
  }
  return context;
};

interface CartProviderProps {
  children: ReactNode;
}

const calculateItemPrice = (fee: Fee, pricingTier: PricingTier): number => {
  switch (pricingTier) {
    case 'full':
      return fee.basePrice;
    case 'reduced':
      return fee.reducedPrice;
    case 'free':
      return fee.freePrice;
    default:
      return fee.basePrice;
  }
};

// Tax applies only to items whose fee is flagged taxable. Schools
// are generally exempt on educational fees but charge tax on
// concession-stand items, merch, etc. The rate is per-school
// (settings.taxRate); a fee with `taxable: true` contributes its
// payable amount to the tax base, others don't.
const calculateCartTotals = (
  items: CartItem[],
  taxRate: number,
): { subtotal: number; tax: number; total: number } => {
  let subtotal = 0;
  let taxableSubtotal = 0;
  items.forEach((item) => {
    const amountToPay = item.partialPaymentAmount ?? item.totalPrice;
    const validAmount = isNaN(amountToPay) || !isFinite(amountToPay) ? 0 : amountToPay;
    subtotal += validAmount;
    if (item.fee.taxable) {
      taxableSubtotal += validAmount;
    }
  });
  const tax = taxableSubtotal * taxRate;
  const total = subtotal + tax;
  return {
    subtotal: Math.max(0, subtotal),
    tax: Math.max(0, tax),
    total: Math.max(0, total),
  };
};

export const CartProvider: React.FC<CartProviderProps> = ({ children }) => {
  const { user, student } = useAuth();
  const { fees, orders } = useData();
  const { settings } = useSchoolSettings();
  const taxRate = settings.taxRate;

  const [cart, setCart] = useState<Cart>({
    id: uuidv4(),
    userId: undefined,
    studentId: undefined,
    items: [],
    subtotal: 0,
    tax: 0,
    total: 0,
    createdAt: new Date(),
    updatedAt: new Date(),
  });

  useEffect(() => {
    setCart(prev => ({
      ...prev,
      userId: user?.id,
      studentId: student?.id,
      updatedAt: new Date(),
    }));
  }, [user, student]);

  const activityPassFeeIds = useMemo(() => {
    return fees.filter(fee => fee.isActivityPass).map(fee => fee.id);
  }, [fees]);

  const hasActivityPass = useMemo(() => {
    const hasInCart = cart.items.some(item => activityPassFeeIds.includes(item.feeId));
    if (hasInCart) return true;

    if (student?.id) {
      const studentOrders = orders.filter(
        order => order.studentId === student.id && order.paymentStatus === 'completed'
      );
      for (const order of studentOrders) {
        if (order.items?.some(item => activityPassFeeIds.includes(item.feeId))) {
          return true;
        }
      }
    }

    return false;
  }, [cart.items, student?.id, activityPassFeeIds, orders]);

  const getActivityPassQuantityUsed = useCallback((feeId: string): number => {
    const cartItem = cart.items.find(item => item.feeId === feeId);
    const cartQuantityUsed = cartItem?.activityPassQuantityUsed || 0;

    let orderQuantityUsed = 0;
    if (student?.id) {
      const studentOrders = orders.filter(
        order => order.studentId === student.id && order.paymentStatus === 'completed'
      );
      for (const order of studentOrders) {
        const orderItem = order.items?.find(item => item.feeId === feeId);
        if (orderItem) {
          orderQuantityUsed += orderItem.quantity;
        }
      }
    }

    return cartQuantityUsed + orderQuantityUsed;
  }, [cart.items, student?.id, orders]);

  const getActivityPassQuantityRemaining = useCallback((feeId: string): number => {
    const fee = fees.find(f => f.id === feeId);
    if (!fee?.activityPassPricing?.enabled) return 0;

    const maxQuantity = fee.activityPassPricing.quantity;
    const used = getActivityPassQuantityUsed(feeId);
    return Math.max(0, maxQuantity - used);
  }, [fees, getActivityPassQuantityUsed]);

  const addToCart = useCallback((fee: Fee, quantity: number, pricingTier: PricingTier, questionnaireResponses?: QuestionnaireResponse, signatureData?: SignatureData, partialPaymentAmount?: number, customUnitPrice?: number, partialPaymentPercentage?: number, amountPaid?: number, activityPassInfo?: ActivityPassInfo) => {
    setCart(prev => {
      const existingItemIndex = prev.items.findIndex(
        item => item.feeId === fee.id && item.pricingTier === pricingTier && !item.questionnaireResponses && !item.signatureData
      );

      let newItems: CartItem[];

      if (existingItemIndex >= 0 && !fee.questionnaire && !fee.termsAndConditions && !fee.allowsCustomAmount) {
        newItems = [...prev.items];
        const existingItem = newItems[existingItemIndex];
        const newQuantity = existingItem.quantity + quantity;
        const newTotalPrice = existingItem.unitPrice * newQuantity;
        let newPartialPaymentAmount: number | undefined;

        if (partialPaymentPercentage !== undefined && fee.allowsPartialPayment) {
          newPartialPaymentAmount = (newTotalPrice * partialPaymentPercentage) / 100;
        } else if (existingItem.partialPaymentPercentage !== undefined && fee.allowsPartialPayment) {
          newPartialPaymentAmount = (newTotalPrice * existingItem.partialPaymentPercentage) / 100;
        } else {
          newPartialPaymentAmount = fee.allowsPartialPayment
            ? (partialPaymentAmount ?? newTotalPrice)
            : (partialPaymentAmount ?? existingItem.partialPaymentAmount);
        }

        newItems[existingItemIndex] = {
          ...existingItem,
          quantity: newQuantity,
          totalPrice: newTotalPrice,
          partialPaymentAmount: newPartialPaymentAmount,
          partialPaymentPercentage: partialPaymentPercentage ?? existingItem.partialPaymentPercentage,
        };
      } else {
        let unitPrice = customUnitPrice ?? calculateItemPrice(fee, pricingTier);
        unitPrice = isNaN(unitPrice) || !isFinite(unitPrice) || unitPrice < 0 ? 0 : unitPrice;
        let totalPrice = unitPrice * quantity;

        if (activityPassInfo) {
          totalPrice = activityPassInfo.totalPrice;
        }

        const newItem: CartItem = {
          id: uuidv4(),
          feeId: fee.id,
          fee,
          quantity,
          pricingTier,
          unitPrice,
          totalPrice,
          partialPaymentAmount: fee.allowsPartialPayment ? (partialPaymentAmount ?? totalPrice) : partialPaymentAmount,
          partialPaymentPercentage,
          amountPaid,
          questionnaireResponses,
          signatureData,
          activityPassQuantityUsed: activityPassInfo?.activityPassQty,
          activityPassDiscount: activityPassInfo?.activityPassDiscount,
        };
        newItems = [...prev.items, newItem];
      }

      const { subtotal, tax, total } = calculateCartTotals(newItems, taxRate);

      return {
        ...prev,
        items: newItems,
        subtotal,
        tax,
        total,
        updatedAt: new Date(),
      };
    });
  }, []);

  const removeFromCart = useCallback((itemId: string) => {
    setCart(prev => {
      const newItems = prev.items.filter(item => item.id !== itemId);
      const { subtotal, tax, total } = calculateCartTotals(newItems, taxRate);

      return {
        ...prev,
        items: newItems,
        subtotal,
        tax,
        total,
        updatedAt: new Date(),
      };
    });
  }, []);

  const calculateActivityPassPricing = useCallback((fee: Fee, quantity: number, pricingTier: PricingTier, currentCartItems: CartItem[], itemId?: string): { totalPrice: number; activityPassQty: number; activityPassDiscount: number } => {
    const basePrice = pricingTier === 'full' ? fee.basePrice : pricingTier === 'reduced' ? fee.reducedPrice : fee.freePrice;

    if (!hasActivityPass || !fee.activityPassPricing?.enabled) {
      return {
        totalPrice: basePrice * quantity,
        activityPassQty: 0,
        activityPassDiscount: 0,
      };
    }

    const activityPassPrice = fee.activityPassPricing.isFree ? 0 : fee.activityPassPricing.price;
    const maxActivityPassQty = fee.activityPassPricing.quantity;

    let usedInOtherItems = 0;
    currentCartItems.forEach(cartItem => {
      if (cartItem.feeId === fee.id && cartItem.id !== itemId && cartItem.activityPassQuantityUsed) {
        usedInOtherItems += cartItem.activityPassQuantityUsed;
      }
    });

    let usedInOrders = 0;
    if (student?.id) {
      const studentOrders = orders.filter(
        order => order.studentId === student.id && order.paymentStatus === 'completed'
      );
      for (const order of studentOrders) {
        const orderItem = order.items?.find(oi => oi.feeId === fee.id);
        if (orderItem) {
          usedInOrders += orderItem.quantity;
        }
      }
    }

    const totalUsed = usedInOtherItems + usedInOrders;
    const remaining = Math.max(0, maxActivityPassQty - totalUsed);
    const activityPassQty = Math.min(remaining, quantity);
    const regularQty = quantity - activityPassQty;

    const activityPassTotal = activityPassQty * activityPassPrice;
    const regularTotal = regularQty * basePrice;
    const totalPrice = activityPassTotal + regularTotal;
    const fullPrice = basePrice * quantity;
    const activityPassDiscount = fullPrice - totalPrice;

    return {
      totalPrice,
      activityPassQty,
      activityPassDiscount,
    };
  }, [hasActivityPass, student?.id, orders]);

  const updateQuantity = useCallback((itemId: string, quantity: number) => {
    if (quantity <= 0) return;

    setCart(prev => {
      const newItems = prev.items.map(item => {
        if (item.id === itemId) {
          let newTotalPrice = item.unitPrice * quantity;
          let newActivityPassQty = item.activityPassQuantityUsed;
          let newActivityPassDiscount = item.activityPassDiscount;

          if (item.fee.activityPassPricing?.enabled && hasActivityPass) {
            const pricingResult = calculateActivityPassPricing(item.fee, quantity, item.pricingTier, prev.items, itemId);
            newTotalPrice = pricingResult.totalPrice;
            newActivityPassQty = pricingResult.activityPassQty;
            newActivityPassDiscount = pricingResult.activityPassDiscount;
          }

          let newPartialPaymentAmount = item.partialPaymentAmount;

          if (item.fee.allowsPartialPayment && item.partialPaymentPercentage !== undefined) {
            newPartialPaymentAmount = (newTotalPrice * item.partialPaymentPercentage) / 100;
          } else if (item.fee.allowsPartialPayment) {
            if (newPartialPaymentAmount !== undefined && newPartialPaymentAmount > newTotalPrice) {
              newPartialPaymentAmount = newTotalPrice;
            } else if (newPartialPaymentAmount === item.totalPrice) {
              newPartialPaymentAmount = newTotalPrice;
            }
          }

          return {
            ...item,
            quantity,
            totalPrice: newTotalPrice,
            partialPaymentAmount: newPartialPaymentAmount,
            activityPassQuantityUsed: newActivityPassQty,
            activityPassDiscount: newActivityPassDiscount,
          };
        }
        return item;
      });

      const { subtotal, tax, total } = calculateCartTotals(newItems, taxRate);

      return {
        ...prev,
        items: newItems,
        subtotal,
        tax,
        total,
        updatedAt: new Date(),
      };
    });
  }, [hasActivityPass, calculateActivityPassPricing]);

  const updatePartialPayment = useCallback((itemId: string, partialPaymentAmount: number | undefined) => {
    setCart(prev => {
      const newItems = prev.items.map(item => {
        if (item.id === itemId) {
          return {
            ...item,
            partialPaymentAmount,
          };
        }
        return item;
      });

      const { subtotal, tax, total } = calculateCartTotals(newItems, taxRate);

      return {
        ...prev,
        items: newItems,
        subtotal,
        tax,
        total,
        updatedAt: new Date(),
      };
    });
  }, []);

  const clearCart = useCallback(() => {
    setCart(prev => ({
      ...prev,
      items: [],
      subtotal: 0,
      tax: 0,
      total: 0,
      updatedAt: new Date(),
    }));
  }, []);

  const itemCount = cart.items.reduce((sum, item) => sum + item.quantity, 0);

  const value: CartContextType = {
    cart,
    addToCart,
    removeFromCart,
    updateQuantity,
    updatePartialPayment,
    clearCart,
    itemCount,
    hasActivityPass,
    getActivityPassQuantityUsed,
    getActivityPassQuantityRemaining,
  };

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
};
